import React, {useMemo} from "react";
import IconButton,  {IconButtonPropTypes} from '../IconButton/IconButton';
import classNames from "classnames";
import PropTypes from 'prop-types';
import './ActionsList.scss';

const acceptedEvents = ['onClick', 'onMouseEnter', 'onMouseLeave'];

export const AXIS_VERTICAL = 'vertical';
export const AXIS_HORIZONTAL = 'horizontal';
export const TYPE_DEFAULT = 'default';
export const TYPE_SECONDARY = 'secondary';
export const AXIS = [AXIS_VERTICAL, AXIS_HORIZONTAL];

export const getOffsetStyle = (type, offset) => {
  if (AXIS.some(el => el === type) && type === AXIS_HORIZONTAL) {
    return {marginRight: offset}
  } else if (type === AXIS_VERTICAL) {
    return {marginBottom: offset}
  }
}

export const reduceHandlers = el => {
  return acceptedEvents
    .reduce((acc, handler) => {
      if (typeof el[handler] === 'function') {
        acc[handler] = () => el[handler](el);
      }
      return acc;
    }, {});
}

const getActionsItemText = el => {
  if(el.text) {
    if(typeof el.text === 'string') {
      return el.text
    }
    if(typeof el.text === 'object') {
      return el.active ? el.text.disabled : el.text.activated
    }
  }
  return false
}

const getActionsIcon = el => {
  if(el.iconData.default && el.iconData.disabled) {
    return {
      ...el.iconData,
      IconComponent: el.active
        ? el.iconData.disabled
        : el.iconData.default
    }
  } else {
    return el.iconData
  }
}

export function ActionItem(el) {
  const itemHandlers = reduceHandlers(el);
  const text = useMemo(() => getActionsItemText(el), [el]);
  const IconComponentData = useMemo(() => getActionsIcon(el), [el]);

  return (
    <>
      {
        el.visible && <div
          {...itemHandlers}
          className={classNames(['actions-item', el.id, el.className], {
            'active': el.active,
          })}
          key={el.id}
          style={el.styles}
        >
          <IconButton
            fillHover={"#fff"}
            {...IconComponentData}
          />
          {
            text
              ? <span className="actions-text">{text}</span>
              : ''
          }
        </div>
      }
    </>
  )
}

export default function ActionsList(props) {
  const {
    items = [],
    offset = 0,
    type = TYPE_DEFAULT,
    axis = AXIS_HORIZONTAL,
    className = '',
    reverse = false,
    children,
  } = props;

  const styles = offset !== 0 ? getOffsetStyle(axis, offset) : {};
  const listHandlers = reduceHandlers(props);
  
  return (
    <div
      {...listHandlers}
      className={classNames(['actions-container', className, type])}>
      {
        items.length && <div className={classNames(['actions-list', axis], {
          reverse
        })}>
          {
            items.map(el => <ActionItem
              styles={styles}
              key={el.id}
              reverse
              {...el}
            />)
          }
        </div>
      }
      {children}
    </div>
  )
}

const ActionItemPropType = PropTypes.shape({
  id: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  action: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      activated: PropTypes.string.isRequired,
      disabled: PropTypes.string.isRequired,
    }),
  ]),
  style: PropTypes.object,
  visible: PropTypes.bool,
  active: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
  ]),
  iconData: IconButtonPropTypes.isRequired,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  reverse: PropTypes.bool,
});

ActionsList.propTypes = {
  items: PropTypes.arrayOf(ActionItemPropType).isRequired,
  offset: PropTypes.number,
  type: PropTypes.oneOf([
    TYPE_DEFAULT,
    TYPE_SECONDARY,
  ]),
  axis: PropTypes.oneOf([
    AXIS_VERTICAL,
    AXIS_HORIZONTAL,
  ]),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
};

ActionItem.propTypes = ActionItemPropType.isRequired;
